/**
 * StatusBadge — colored badge variants (success/warning/danger/info/neutral/primary/purple)
 *
 * `purple` eklendi (Muhasebe Faz 3.7b, plan §11.6): `interest` (Faiz Isliyor)
 * ve `legal` (Hukuki Takip) durumlari icin. Kontrast dogrulamasi (purple-700
 * on purple-100) ~5.9:1 — WCAG 2.1 AA (4.5:1) ustunde.
 *
 * `icon` prop'u opsiyoneldir ve geriye donuk uyumludur — vermezseniz eski
 * davranis (sadece dot + label) aynen calisir. Plan §11.6 `AlertTriangle`/
 * `Scale`/`Scissors`/`Merge` istiyor; bu turda sadece SLOT eklendi, ikon
 * secimi cagirana birakildi (Scissors/Merge Faz 4 konusu, bu turda kullanilmadi).
 *
 * @example
 * <StatusBadge variant="success" label={t('admin.status_active')} />
 * <StatusBadge variant="danger" label={t('admin.status_inactive')} dot />
 * <StatusBadge variant="purple" label={t('accounting.status_legal')} icon={Scale} />
 */

import PropTypes from 'prop-types';
import { cn } from '@/lib/utils';

const VARIANTS = {
  success: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  warning: 'bg-amber-100 text-amber-700 border-amber-200',
  danger:  'bg-red-100 text-red-700 border-red-200',
  info:    'bg-sky-100 text-sky-700 border-sky-200',
  neutral: 'bg-gray-100 text-gray-600 border-gray-200',
  primary: 'bg-[var(--brand-primary)]/10 text-[var(--brand-primary)] border-[var(--brand-primary)]/20',
  purple:  'bg-purple-100 text-purple-700 border-purple-200',
};

const DOT_COLORS = {
  success: 'bg-emerald-500',
  warning: 'bg-amber-500',
  danger:  'bg-red-500',
  info:    'bg-sky-500',
  neutral: 'bg-gray-400',
  primary: 'bg-[var(--brand-primary)]',
  purple:  'bg-purple-500',
};

export default function StatusBadge({ variant, label, dot, icon: Icon, className }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full border text-xs font-bold',
        VARIANTS[variant] || VARIANTS.neutral,
        className,
      )}
      role="status"
    >
      {dot && (
        <span
          className={cn('w-1.5 h-1.5 rounded-full flex-shrink-0', DOT_COLORS[variant] || DOT_COLORS.neutral)}
          aria-hidden
        />
      )}
      {Icon && <Icon size={13} strokeWidth={2.5} className="flex-shrink-0" aria-hidden />}
      {label}
    </span>
  );
}

StatusBadge.propTypes = {
  variant: PropTypes.oneOf(['success', 'warning', 'danger', 'info', 'neutral', 'primary', 'purple']),
  label: PropTypes.string.isRequired,
  /** Show animated dot indicator */
  dot: PropTypes.bool,
  /** Opsiyonel lucide-react ikon bileseni (ör. AlertTriangle, Scale) — label'dan once render edilir */
  icon: PropTypes.elementType,
  className: PropTypes.string,
};

StatusBadge.defaultProps = {
  variant: 'neutral',
  dot: false,
  icon: null,
};
